import type { LeadActivityDto, LeadVisitorInterestDto, UpdateLeadInput } from "@mvk/shared";
import type { Lead, LeadActivity, LeadStatus } from "../types";
import { isApiEnabled } from "../api/config";
import { adminAuthStorage, visitorAuthStorage } from "../api/authStorage";
import { leadsApi, leadDtoToUiLead } from "../api/leads";

function mapActivityDto(dto: LeadActivityDto): LeadActivity {
  return {
    id: dto.id,
    leadId: dto.leadId,
    activityType: dto.activityType,
    timestamp: dto.timestamp,
    description: dto.description,
    metadata: dto.metadata,
  };
}

function isAuthenticated(audience: "visitor" | "admin") {
  return audience === "admin"
    ? adminAuthStorage.isAuthenticated()
    : visitorAuthStorage.isAuthenticated();
}

function requireApi(audience: "visitor" | "admin"): void {
  if (!isApiEnabled() || !isAuthenticated(audience)) {
    throw new Error("[leadsService] API auth required");
  }
}

export type LeadDetail = {
  lead: Lead;
  activities: LeadActivity[];
  visitorInterest?: LeadVisitorInterestDto;
};

export const leadsService = {
  async listByCompany(
    eventId: string,
    companyId: string,
    audience: "visitor" | "admin" = "admin",
    direction: "inbound" | "outbound" | "all" = "all",
    assignedTo?: string | "unassigned",
  ): Promise<{ leads: Lead[]; myAssigneeId?: string }> {
    requireApi(audience);
    const data = await leadsApi.list(eventId, audience, {
      companyId,
      direction,
      assignedTo,
    });
    return {
      leads: data.leads.filter((l) => l.companyId === companyId).map(leadDtoToUiLead),
      myAssigneeId: data.myAssigneeId,
    };
  },

  async exportCsv(
    eventId: string,
    companyId: string,
    audience: "visitor" | "admin" = "admin",
    options?: {
      direction?: "inbound" | "outbound" | "all";
      assignedTo?: string | "unassigned";
    },
  ): Promise<void> {
    requireApi(audience);
    await leadsApi.exportCsv(eventId, audience, {
      companyId,
      direction: options?.direction,
      assignedTo: options?.assignedTo,
    });
  },

  async getDetail(
    eventId: string,
    id: string,
    audience: "visitor" | "admin" = "admin",
  ): Promise<LeadDetail | undefined> {
    requireApi(audience);
    try {
      const data = await leadsApi.get(eventId, id, audience);
      return {
        lead: leadDtoToUiLead(data.lead),
        activities: data.activities.map(mapActivityDto),
        visitorInterest: data.visitorInterest,
      };
    } catch {
      return undefined;
    }
  },

  async getById(
    eventId: string,
    id: string,
    audience: "visitor" | "admin" = "admin",
  ): Promise<Lead | undefined> {
    const detail = await this.getDetail(eventId, id, audience);
    return detail?.lead;
  },

  async getActivities(
    eventId: string,
    leadId: string,
    audience: "visitor" | "admin" = "admin",
  ): Promise<LeadActivity[]> {
    const detail = await this.getDetail(eventId, leadId, audience);
    return detail?.activities ?? [];
  },

  async getByStatus(
    eventId: string,
    companyId: string,
    status: LeadStatus,
    audience: "visitor" | "admin" = "admin",
  ): Promise<Lead[]> {
    const { leads } = await this.listByCompany(eventId, companyId, audience);
    return leads.filter((l) => l.status === status);
  },

  async update(
    eventId: string,
    id: string,
    updates: Partial<Lead>,
    audience: "visitor" | "admin" = "admin",
  ): Promise<Lead | null> {
    requireApi(audience);
    const body: UpdateLeadInput = {};
    if (updates.status !== undefined) body.status = updates.status;
    if (updates.notes !== undefined) body.notes = updates.notes;
    if (updates.rating !== undefined) body.rating = updates.rating ?? null;
    if (updates.tags !== undefined) body.tags = updates.tags;
    if (updates.assignedTo !== undefined) body.assignedTo = updates.assignedTo ?? null;

    const data = await leadsApi.update(eventId, id, body, audience);
    return leadDtoToUiLead(data.lead);
  },

  async getStats(eventId: string, companyId: string, audience: "visitor" | "admin" = "admin") {
    const { leads } = await this.listByCompany(eventId, companyId, audience);
    return {
      total: leads.length,
      new: leads.filter((l) => l.status === "new").length,
      contacted: leads.filter((l) => l.status === "contacted").length,
      inProgress: leads.filter((l) => l.status === "in_progress").length,
      qualified: leads.filter((l) => l.status === "qualified").length,
      closed: leads.filter((l) => l.status === "closed").length,
      rejected: leads.filter((l) => l.status === "rejected").length,
      avgRating:
        leads.filter((l) => l.rating).reduce((sum, l) => sum + (l.rating || 0), 0) /
        (leads.filter((l) => l.rating).length || 1),
    };
  },

  async addNote(
    eventId: string,
    leadId: string,
    note: string,
    audience: "visitor" | "admin" = "admin",
  ): Promise<void> {
    await this.update(eventId, leadId, { notes: note }, audience);
  },
};
